import React from 'react'

import DOMPurify from 'dompurify'

import {
  AlignItems,
  Box,
  Color,
  Display,
  Divider,
  FlexDirection,
  FontFamily,
  FontWeight,
  GridDisplay,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type {
  BasketSummaryBaseLine,
  BasketSummaryContentBlock,
  BasketSummaryLine,
  BasketSummaryRecipeLine,
  BasketSummaryTotalLine,
} from '@library/api-menu-experience'

const ValueBannerMessage = withCitrusTheme(Text, (theme) => ({
  '& span': {
    color: theme.color(Color.Success_900),
    fontWeight: FontWeight.Bold,
  },
}))

const DefaultLine = ({ line }: { line: BasketSummaryBaseLine }) => (
  <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween} gap={4}>
    <Text>{line.label}</Text>
    <Text>{line.price}</Text>
  </Box>
)

const DiscountLine = ({ line }: { line: BasketSummaryBaseLine }) => (
  <Box
    bg={Color.Success_50}
    data-testid="basket-summary-discount"
    display={Display.Flex}
    justifyContent={JustifyContent.SpaceBetween}
    paddingH={4}
    paddingV={3}
    style={{ borderRadius: '8px' }}
  >
    <Text color={Color.Success_900} fontWeight={FontWeight.Bold}>
      {line.label}
    </Text>
    <Text color={Color.Success_900} fontWeight={FontWeight.Bold}>
      {line.price}
    </Text>
  </Box>
)

const RecipeLine = ({ line, index }: { line: BasketSummaryRecipeLine; index: number }) => (
  <Box
    display={GridDisplay.Grid}
    gridTemplateColumns="minmax(0, 68%) minmax(0, 1fr)"
    gap={4}
    style={{ alignItems: 'start' }}
  >
    <Box minWidth="0">
      <Text
        data-testid={`basket-summary-recipe-title-${index}`}
        fontWeight={FontWeight.Bold}
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {line.label}
      </Text>
      {line.description && (
        <Text color={Color.ColdGrey_600} size={2}>
          {line.description}
        </Text>
      )}
    </Box>
    <Text
      data-testid={`basket-summary-recipe-price-${index}`}
      fontWeight={FontWeight.Bold}
      style={{ justifySelf: 'end' }}
    >
      {line.price}
    </Text>
  </Box>
)

const TotalLine = ({ line }: { line: BasketSummaryTotalLine }) => (
  <Box
    display={Display.Flex}
    justifyContent={JustifyContent.SpaceBetween}
    gap={4}
    paddingBottom={0}
    paddingV={2}
  >
    <Text fontWeight={FontWeight.Bold} size={5}>
      {line.label}
    </Text>
    <Box display={Display.Flex} gap={2} alignItems={AlignItems.Center}>
      {line.originalPrice && (
        <Text size={5} style={{ textDecoration: 'line-through' }}>
          {line.originalPrice}
        </Text>
      )}
      <Text
        color={line.originalPrice ? Color.Success_900 : undefined}
        data-testid="basket-summary-final-total"
        fontWeight={FontWeight.Bold}
        size={5}
      >
        {line.price}
      </Text>
    </Box>
  </Box>
)

const isRecipeLine = (line: BasketSummaryLine): line is BasketSummaryRecipeLine =>
  line.displayType === 'recipe' &&
  'id' in line &&
  typeof line.id === 'string' &&
  'urn' in line &&
  typeof line.urn === 'string'

const isTotalLine = (line: BasketSummaryLine): line is BasketSummaryTotalLine =>
  line.displayType === 'total'

const BasketSummaryLineItem = ({ index, line }: { index: number; line: BasketSummaryLine }) => {
  if (isRecipeLine(line)) return <RecipeLine index={index} line={line} />
  if (line.displayType === 'discount') return <DiscountLine line={line} />
  if (isTotalLine(line)) return <TotalLine line={line} />

  return <DefaultLine line={line} />
}

type BasketSummaryProps = {
  contentBlock: BasketSummaryContentBlock
}

export const BasketSummary = ({ contentBlock }: BasketSummaryProps) => {
  let recipeIndex = 0

  return (
    <Box
      bg={Color.White}
      boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
      color={Color.ColdGrey_800}
      data-testid="basket-summary"
      paddingH={5}
      paddingV={5}
      style={{ borderRadius: '8px' }}
    >
      <Box alignItems={AlignItems.Center} display={Display.Flex} gap={3}>
        <Icon name="payment" variant={IconVariant.Default} size={6} />
        <Text fontWeight={FontWeight.Bold} size={5}>
          {contentBlock.title}
        </Text>
      </Box>

      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={5}
        paddingBottom={0}
        paddingV={5}
      >
        <Text size={3} color={Color.ColdGrey_600}>
          {contentBlock.description}
        </Text>

        {contentBlock.sections.map((section, sectionIndex) => (
          <React.Fragment key={`section-${sectionIndex}`}>
            {sectionIndex > 0 && (
              <Box data-testid={`basket-summary-section-divider-${sectionIndex}`}>
                <Divider color={Color.ColdGrey_100} />
              </Box>
            )}
            <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
              {section.lines.map((line, lineIndex) => {
                const index = isRecipeLine(line) ? recipeIndex++ : lineIndex

                return (
                  <BasketSummaryLineItem
                    key={`${line.displayType}-${line.label}-${lineIndex}`}
                    index={index}
                    line={line}
                  />
                )
              })}
            </Box>
          </React.Fragment>
        ))}

        {contentBlock.valueBanner && (
          <Box
            alignItems={AlignItems.Center}
            bg={Color.Success_50}
            color={Color.Success_900}
            data-testid="basket-summary-value-banner"
            display={Display.Flex}
            gap={2}
            paddingH={4}
            paddingV={3}
            style={{ borderRadius: '8px' }}
          >
            <Icon name="offer_pound" size={8} variant={IconVariant.Inherit} />
            <ValueBannerMessage
              color={Color.ColdGrey_800}
              fontFamily={FontFamily.Bold}
              fontWeight={FontWeight.Bold}
              size={4}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(contentBlock.valueBanner.descriptionHTML),
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
